/**
 * v2 storage keys — namespaced under `tlb_v2_*` so they NEVER collide with
 * the v1 `PatientTalbinahPortal*` keys. v2 owns an isolated storage surface.
 */
export const StorageKeys = {
  LANGUAGE: 'tlb_v2_language',
  THEME: 'tlb_v2_theme',
  SPLASH_SEEN: 'tlb_v2_splash_seen',
  ONBOARDING_COMPLETED: 'tlb_v2_onboarding_completed',
  SUBSCRIPTION_TIER: 'tlb_v2_subscription',
  SUBSCRIPTION_TRIAL_EXPIRES: 'tlb_v2_trial_expires',
  SUBSCRIPTION_NEXT_BILL: 'tlb_v2_next_bill',
  // A paid checkout handed off to the hosted gateway `{ planId, cycle, startedAt }`.
  // Written just before the tab leaves for the payment page, so the return trip
  // (or a browser Back) knows to VERIFY with `GET /api/v3/subscription` instead
  // of assuming the charge went through. Expires on its own after 30 minutes.
  SUBSCRIPTION_CHECKOUT: 'tlb_v2_subscription_checkout',
  // The user's gender SELECTION (`male` | `female` | `na`) — drives the
  // masculine-neutral-base / feminine-only-when-chosen copy rule via the
  // GenderService + `tg` pipe. Mirrors the design's `tlb_profile.gender` store;
  // survives reload so the chosen voice applies instantly on next paint.
  GENDER: 'tlb_v2_gender',
  // v2-native auth (passwordless OTP → Sanctum bearer). NEVER shares v1's
  // `PatientTalbinahPortalToken`; the token lives only under the v2 namespace.
  AUTH_TOKEN: 'tlb_v2_auth_token',
  AUTH_USER: 'tlb_v2_auth_user',
  // Khoyek reminders (K1 bell panel). The rows and their dismissals live on the
  // ACCOUNT (`/api/v3/khoyek/reminders`), so nothing about them is stored here —
  // this is only a mirror of the three quick-toggles' last-known SERVER value
  // (`groups.daily` / `khoyek_tools` / `before`), so re-opening the sheet paints
  // the switches before the preferences read lands.
  KHOYEK_REMINDER_TOGGLES: 'tlb_v2_khoyek_reminder_toggles',
  // Profile PR5/PR4 preference snapshots (persisted feature-locally by the
  // ProfileFacade). Per-user data → purged on sign-out (see below).
  PROFILE_NOTIF_PREFS: 'tlb_v2_profile_notif',
  PROFILE_PRIVACY_PREFS: 'tlb_v2_profile_privacy',
  // PR_DEVICES wearable/smartwatch connection state (ProfileFacade stub-service).
  WEARABLE: 'tlb_v2_wearable',
  // The FCM WEB token last registered with `POST /v3/profile/notifications/token`
  // (NB6a web push). Only a de-dupe marker — the token itself is minted by FCM and
  // owned by the server; this just stops a repeat visit from re-POSTing an
  // unchanged value. Per-user → purged on sign-out so the next account registers
  // this device under its own id.
  PUSH_TOKEN: 'tlb_v2_push_token',
  // The RAW FCM web token last minted by `getToken` for THIS BROWSER.
  //
  // Deliberately separate from PUSH_TOKEN (which is a per-user de-dupe marker,
  // `${userId}:${token}`, and is purged on sign-out) and deliberately NOT in the
  // purge list below: FCM hands the same token to the same browser no matter who is
  // signed in, so the token belongs to the DEVICE, not to the account.
  //
  // It exists so sign-in can carry `fcm_token` on `POST /api/v3/auth/verify-otp`
  // without minting anything: the token is already known before the session exists,
  // which is the only window in which a fresh session would otherwise have no push
  // target at all (the device row is registered a moment later by the shell's
  // `syncIfPermitted()`). Only ever WRITTEN after a successful mint — never a
  // prompt, never a request. See `docs/FCM-TOKEN-USAGE-LOGIC.md` §2/§4a.
  PUSH_DEVICE_TOKEN: 'tlb_v2_push_device_token',
  // When the reader last dismissed the shell's soft «فعّل الإشعارات» ask (epoch ms).
  // A device property like PUSH_DEVICE_TOKEN — the browser's permission is per
  // browser, so the ask is too — and therefore not purged on sign-out.
  PUSH_PROMPT_DISMISSED_AT: 'tlb_v2_push_prompt_dismissed_at',
  // The user's personalized Khoyek look (صمّم خويك · color/shape/accessory) chosen
  // in onboarding (KD) and re-editable from Profile. Local source-of-truth for every
  // "identity" orb across the app; best-effort synced to the server post-auth via
  // PATCH /api/v3/users/{id}/khoyek-style. Per-user → purged on sign-out (below).
  KHOYEK_STYLE: 'tlb_v2_khoyek_style',
  // Care Doctor-AI intake chat id — lives in sessionStorage (survives a same-tab
  // reload); user-scoped conversation state, so purged on sign-out too.
  CARE_INTAKE_SESSION: 'tlb_intake_session',
  // Care hand-off crumbs (transient, sessionStorage) — cleared on sign-out for hygiene.
  CARE_SHARE_ENTRY: 'tlb_share_entry',
  CARE_KHOYEK_TOOL: 'tlb_khoyek_tool',
  // Onboarding A5b «احجز» intent (AI doctor key + picked slot) held across the
  // phone/OTP hop (sessionStorage — survives a same-tab reload); cleared by the
  // onboarding flow on booking success / skip. Deliberately NOT in the sign-out purge:
  // a 401 mid-booking clears the session, and the intent must survive the re-verify.
  ONB_PENDING_BOOKING: 'tlb_onb_pending_booking',
  // The B2B Home Hub's last-active tab (start · wheel · eng). A device/UI
  // preference like THEME/LANGUAGE — not identity data — so it survives sign-out.
  HOME_HUB_ACTIVE_VIEW: 'tlb_v2_home_hub_view',
  // ── MVP (docs/mvp/MVP-README.md) ──────────────────────────────────────────
  // A random per-BROWSER id (onboarding A/B bucket + public «كلمة اليوم» guesses).
  // A device property, like PUSH_DEVICE_TOKEN — survives sign-out.
  DEVICE_ID: 'tlb_v2_device_id',
  // An invite code from the entry link (`?ref=` · `?r=` · `/invite?code=`), kept until
  // sign-up and sent once as `referral_code` on OTP verify (BE11). Exists before any
  // account, so it is device-level and NOT in the user-scoped purge list.
  REFERRAL_CODE: 'tlb_v2_referral_code',
  // The sticky onboarding A/B bucket `{ bucket, source }` (MVP §6 `onb_split`).
  // Device-level until sign-up, then sent with the onboarding answers.
  ONB_SPLIT: 'tlb_v2_onb_split',
  // QA-only bucket override (`?onb_split=control|khoyek_first` on /onboarding).
  ONB_SPLIT_OVERRIDE: 'tlb_v2_onb_split_override',
  // Onboarding outcome `{ path: 'A'|'B', intents: string[] }` — read by K6 (MVP §5)
  // for the intent-ordered third message and the free-session line.
  ONB_OUTCOME: 'tlb_v2_onb_outcome',
  // Last good `GET /home` snapshot shown instantly / offline (MVP §1 H9/H10).
  HOME_CACHE: 'tlb_home_cache',
  // Moods tapped while offline, flushed on reconnect (MVP §1 H10).
  MOOD_QUEUE: 'tlb_mood_queue',
  // In-app «كلمة اليوم» progress + small per-day history (MVP §2).
  KALMA_PROGRESS: 'tlb_v2_kalma',
  // «كلمة اليوم» result outbox `{ pending, sent }`: finished games not yet accepted by
  // `POST /api/v3/public/kalma/result` (retried on next open / reconnect) + accepted day ids.
  KALMA_OUTBOX: 'tlb_v2_kalma_outbox',
  // B10 Premium offer `{ shown, pending }`: triggers whose sheet was already shown + the
  // `POST /api/v3/membership-offer/seen` calls not yet accepted (retried on Home / reconnect).
  MEMBERSHIP_OFFER_OUTBOX: 'tlb_membership_offer',
  // «احفظ نتيجتك» — a guest's request to move today's public board onto the account
  // they are about to sign in to (`{ dayId, lang }`, one-shot). Written while a
  // GUEST, so device-level — never in the user-scoped purge list.
  KALMA_CLAIM: 'tlb_v2_kalma_claim',
  // Tools usage/feedback mirror for `tools_mode` (MVP §4).
  TOOLS_USAGE: 'tlb_v2_tools_usage',
  // Khoyek MVP UI memory: tool-card snoozes, game-card ignores, proactive dismissals (MVP §4/§5).
  KHOYEK_MVP: 'tlb_v2_khoyek_mvp',
  // REMOVED — `tlb_v2_wellbeing_scores` / `tlb_v2_wellbeing_checkin_done` /
  // `tlb_v2_engagement_pulse`. The B2B wheel's check-in and the weekly pulse
  // were localStorage-only while no backend existed for them. They are now
  // served by `GET /api/v3/org/wheel` + `POST /org/wheel/check-in` and
  // `GET /org/engagement` + `POST /org/engagement/pulse`, which are per-ACCOUNT
  // and cross-device. Keeping a local mirror would have meant two answers to
  // "what did I score today?", and the local one would win on first paint —
  // including for a member whose answers the server silently dropped (§3.2).
  // NOTE: translation & home-content cache keys live in
  // `core/constants/v2-cache-keys.constants.ts` (language-scoped) — not here.
} as const;

export type StorageKey = (typeof StorageKeys)[keyof typeof StorageKeys];

/**
 * Prefix for `tbDraggable` parked positions — one entry per named draggable
 * (`tlb_v2_drag_care-crisis-fab`), holding `{ xr, yr }` fractions of its bounds
 * box. A DEVICE preference (where this person likes the floating button to sit),
 * not identity data → deliberately NOT in the sign-out purge below.
 */
export const DRAG_POSITION_PREFIX = 'tlb_v2_drag_';

/**
 * Identity-bound keys purged on sign-out / account-deletion (`AuthSessionStore.
 * purgeUserScoped`). These carry data ABOUT THE SIGNED-IN PERSON, so leaving
 * them behind would bleed one user's state onto the next on a shared device
 * (previous user's gender voice, paid subscription tier, Khoyek reminders).
 *
 * The AUTH token/user pair is cleared by {@link AuthSessionStore.clear} on both
 * this deliberate purge AND the `401` path, so it is intentionally NOT repeated
 * here.
 *
 * DELIBERATELY EXCLUDED (device / app preferences — NOT tied to identity, so
 * they survive a sign-out so the sign-in screen stays in the chosen language /
 * theme and the intro isn't re-shown):
 *   LANGUAGE · THEME · SPLASH_SEEN · ONBOARDING_COMPLETED
 * The translation & home-content caches (see `v2-cache-keys.constants.ts`) are
 * public, language-scoped content — never per-user — so they also survive.
 */
export const USER_SCOPED_STORAGE_KEYS: readonly string[] = [
  StorageKeys.GENDER,
  StorageKeys.SUBSCRIPTION_TIER,
  StorageKeys.SUBSCRIPTION_TRIAL_EXPIRES,
  StorageKeys.SUBSCRIPTION_NEXT_BILL,
  StorageKeys.SUBSCRIPTION_CHECKOUT,
  StorageKeys.KHOYEK_REMINDER_TOGGLES,
  StorageKeys.PROFILE_NOTIF_PREFS,
  StorageKeys.PROFILE_PRIVACY_PREFS,
  StorageKeys.WEARABLE,
  StorageKeys.KHOYEK_STYLE,
  StorageKeys.PUSH_TOKEN,
  StorageKeys.ONB_OUTCOME,
  StorageKeys.HOME_CACHE,
  StorageKeys.MOOD_QUEUE,
  StorageKeys.KALMA_PROGRESS,
  StorageKeys.KALMA_OUTBOX,
  StorageKeys.MEMBERSHIP_OFFER_OUTBOX,
  StorageKeys.TOOLS_USAGE,
  StorageKeys.KHOYEK_MVP,
  // NOTE: PUSH_DEVICE_TOKEN (the raw FCM token) is deliberately absent — it is a
  // property of the BROWSER, not of the person, and FCM would hand the next user
  // the identical value anyway. What must not survive is the per-user registration
  // marker above; the server-side routing for the account that left is revoked by
  // `WebPushService.unregister()` on sign-out, not by forgetting the token here.
  // NOTE: the B2B wheel scores / check-in flag / pulse answer used to be purged
  // here. They are no longer STORED at all — `GET /api/v3/org/wheel` and
  // `/org/engagement` own that data now (see the keys' own comment above), so
  // there is nothing local left to bleed onto the next user. The hub's ACTIVE
  // TAB is still kept, and still deliberately absent from this list: it is a
  // device preference, like THEME.
] as const;

/**
 * Identity-bound keys that live in **sessionStorage** (not localStorage) — purged
 * on sign-out by passing the session flag to `removeMany`. sessionStorage survives
 * a same-tab reload (our hard-redirect sign-out stays in the same tab), so these
 * would otherwise bleed to the next user exactly like the localStorage set above.
 */
export const USER_SCOPED_SESSION_KEYS: readonly string[] = [
  StorageKeys.CARE_INTAKE_SESSION,
  StorageKeys.CARE_SHARE_ENTRY,
  StorageKeys.CARE_KHOYEK_TOOL,
] as const;
